import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Music2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const seen = (id) => localStorage.getItem(`maestro_tip_${id}`) === "1";
const markSeen = (id) => localStorage.setItem(`maestro_tip_${id}`, "1");

export const MaestroGuide = () => {
  const { user, refreshUser } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [tip, setTip] = useState(null);
  const navTimes = useRef([]);
  const checked = useRef(false);
  const enabled = !!user && user.proactive_guidance !== false
    && !(user && !user.tour_done && loc.pathname === "/home");

  const show = (t) => { if (!seen(t.id)) { markSeen(t.id); setTip(t); } };

  useEffect(() => {
    if (!enabled || tip) return;
    const now = Date.now();
    navTimes.current = [...navTimes.current.filter((t) => now - t < 90000), now];
    if (navTimes.current.length >= 8) {
      show({ id: "lost", text: "Looking for something? I can point the way — try the Get Started guide, or hit the ♪ Meet Maestro button up top and ask me directly.", cta: "Open the guide", to: "/guide" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc, enabled]);

  useEffect(() => {
    if (!enabled || checked.current) return;
    checked.current = true;
    (async () => {
      try {
        const { data: projects } = await api.get("/projects");
        const empty = (projects || []).find((p) => (p.task_count || 0) === 0 && !p.archived);
        if (empty && !seen(`empty-${empty.id}`)) {
          show({ id: `empty-${empty.id}`, text: `I noticed "${empty.name}" doesn't have any tasks yet. Want a hand? Open it and I can draft the whole plan, or use the IO Map builder for an Input → Output structure.`, cta: "Open the project", to: `/projects/${empty.id}` });
          return;
        }
        const { data: cards } = await api.get("/cards");
        const overdue = (Array.isArray(cards) ? cards : []).reduce((a, c) => a + (c.overdue || 0), 0);
        if (overdue >= 5) {
          show({ id: "overdue", text: `There are ${overdue} overdue items across the team. A quick True-Up or a phase Nudge from the resource cards can get fresh statuses flowing into the plan.`, cta: "Open resource cards", to: "/cards" });
        }
      } catch (e) { console.debug("maestro guide check failed", e); }
    })();
  }, [enabled]);

  const turnOff = async () => {
    setTip(null);
    try {
      await api.put("/me/onboarding", { proactive_guidance: false });
      refreshUser();
      toast.info("Proactive tips are off — turn them back on anytime via ♪ Meet Maestro in the header.");
    } catch (e) { toast.error("Couldn't save that preference"); }
  };

  if (!tip) return null;
  return (
    <div className="fixed bottom-20 left-4 z-[90] max-w-sm bg-white border border-slate-200 shadow-[0_16px_48px_rgba(13,30,76,0.22)] overflow-hidden animate-in slide-in-from-bottom-4" data-testid="maestro-tip">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-stretch gap-1 shrink-0 mt-0.5" aria-hidden>
            <span className="w-1.5 bg-[#0d1e4c]" style={{ height: 22 }} />
            <span className="w-1.5 bg-[#2f6bff]" style={{ height: 22 }} />
            <span className="w-1.5 bg-slate-200" style={{ height: 22 }} />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-bold tracking-[0.22em] text-[#2f6bff] mb-1 flex items-center gap-1.5"><Music2 className="h-3 w-3" /> MAESTRO TIP</p>
            <p className="text-sm text-slate-800 leading-relaxed" data-testid="maestro-tip-text">{tip.text}</p>
            <div className="flex items-center gap-4 mt-2.5">
              {tip.to && (
                <button type="button" onClick={() => { setTip(null); nav(tip.to); }} className="text-sm font-bold text-[#2f6bff] hover:text-[#1d4fd8] hover:translate-x-0.5 transition-all" data-testid="maestro-tip-cta">{tip.cta} →</button>
              )}
              <button type="button" onClick={turnOff} className="text-xs text-slate-400 hover:text-slate-600" data-testid="maestro-tip-off">Turn off tips</button>
            </div>
          </div>
          <button type="button" onClick={() => setTip(null)} className="text-slate-400 hover:text-slate-700 shrink-0" data-testid="maestro-tip-dismiss"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="h-1 bg-[#2f6bff]" />
    </div>
  );
};

export default MaestroGuide;
